import { useActiveGlobalImpersonation } from "@client/impersonationRef";
import { useCurrentUser } from "./use-current-user";

/**
 * Returns the effective user — the impersonated user when impersonating,
 * or the real logged-in user when not. This is the client-side equivalent
 * of ctx.getUser() on the server.
 *
 * Use this for identity display and permission checks in the UI.
 * Use useCurrentUser() only for impersonation infrastructure (analytics,
 * the impersonation dropdown itself).
 */
export function useEffectiveUser() {
  // useCurrentUser resolves the underlying user from the offline identity cache
  // when the iPad is offline, so `currentUser` is already offline-stable here —
  // no separate fallback needed (without a user offline the header would render
  // no nav tabs). Inert on desktop/iPhone and when online.
  const { data: resolvedUser, isLoading, isError } = useCurrentUser();
  const global = useActiveGlobalImpersonation();

  if (!resolvedUser) {
    return { data: null, isLoading, isError };
  }

  if (global) {
    return {
      data: {
        userId: global.userId,
        userName: global.displayName,
        email: null as string | null,
        // The TARGET's own admin flag, captured when impersonation started —
        // mirrors the server's effectiveIsSfcAdmin, so viewing as an admin
        // keeps admin pages accessible (older persisted entries: false).
        isSfcAdmin: global.isSfcAdmin ?? false,
      },
      isLoading: false,
      isError: false,
    };
  }

  return {
    data: {
      userId: resolvedUser.userId,
      userName: resolvedUser.userName,
      email: resolvedUser.email as string | null,
      isSfcAdmin: resolvedUser.isSfcAdmin,
    },
    isLoading: false,
    isError: false,
  };
}
